/**
 * Dashboard map: Google Map with client-style pins + geo clustering.
 * - Far zoom: geographic clusters (grid), label = dominant city/region
 * - Near zoom: viewport-capped individual pins, size scales with zoom
 * - SUPER_ADMIN: paint public map cache first, enrich with admin stations in background
 * - ADMIN / MERCHANT: only own network stations (admin API, parallel pages)
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import { useColorScheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { GoogleMap, Marker, InfoWindow, OverlayViewF, OVERLAY_MOUSE_TARGET } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import * as api from '../../../modules/api';
import { useGoogleMaps } from '../../GoogleMapsProvider';
import { formatApiDate } from '../../utils/formatApiDate';
import pinOnline from '../../assets/map/pin-online.png';
import pinOffline from '../../assets/map/pin-offline.png';
import pinEmpty from '../../assets/map/pin-empty.svg';

const DEFAULT_CENTER = { lat: 40.18, lng: 44.51 };
const DEFAULT_ZOOM = 10;
const MAP_CONTAINER_STYLE = { width: '100%', height: '480px', borderRadius: 8 };
/**
 * Below this zoom → clusters; at/above → individual pins.
 * Keep clusters longer so fitBounds (often zoom 8–11) does not create thousands of Markers at once.
 */
const CLUSTER_MAX_ZOOM = 12;
/** Ignore cluster "clicks" shortly after zoom/wheel (scroll-zoom false positives). */
const CLUSTER_CLICK_GUARD_MS = 450;
/** Parallel admin page fetches (0-based Spring pages). */
const ADMIN_PAGE_SIZE = 100;
const ADMIN_MAX_PAGES = 30;
const ADMIN_FETCH_CONCURRENCY = 5;
/** Soft-cap individual Markers when zoomed in (viewport + this hard max). */
const MAX_VISIBLE_MARKERS = 800;

const DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#1d1d1d' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1d1d1d' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1a1a1a' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3a3a3a' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f1f1f' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1620' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c66' }] },
];

const LIGHT_MAP_STYLES = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
];

function getCurrentRole() {
  try {
    return (localStorage.getItem('role') || '').toUpperCase();
  } catch (_) {
    return '';
  }
}

/** Client uses 20×20; scale down when zoomed out so far view isn't cluttered. */
function pinSizeForZoom(zoom) {
  const z = Number(zoom) || DEFAULT_ZOOM;
  if (z >= 15) return 22;
  if (z >= 13) return 20;
  if (z >= 11) return 16;
  if (z >= 10) return 14;
  return 12;
}

const COUNTRY_LIKE = /^(беларусь|россия|armenia|армения|estonia|эстония|indonesia|индонезия|латвия|lithuania|литва|kazakhstan|казахстан|georgia|грузия)$/i;
const REGION_LIKE = /(?:^|\s)(обл\.?|область|край|респ\.?|республика|ао|округ|р-н|район)(?:\s|$)/i;
const STREET_LIKE = /^(ул\.?|улица|пр\.?|проспект|пер\.?|переулок|ш\.?|шоссе|бул\.?|бульвар|наб\.?|мкр\.?|микрорайон|д\.?|зд\.?|стр\.?|корп)/i;

/**
 * Extract city + region from addresses like:
 * "Россия, Белгородская обл, г Белгород, ул …"
 * "Беларусь, г Минск, …"
 */
export function extractPlaceFromAddress(address) {
  if (address == null || address === '') return { city: null, region: null };
  const raw = String(address).trim();
  if (!raw) return { city: null, region: null };
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);

  let city = null;
  let region = null;

  for (const p of parts) {
    const m = p.match(/^(?:г\.?|город)\s+(.+)$/i);
    if (m) {
      city = m[1].trim();
      break;
    }
  }

  for (const p of parts) {
    if (REGION_LIKE.test(p) && !COUNTRY_LIKE.test(p)) {
      region = p.replace(/^(?:г\.?|город)\s+/i, '').trim();
      break;
    }
  }

  if (!city) {
    for (const p of parts) {
      if (COUNTRY_LIKE.test(p)) continue;
      if (REGION_LIKE.test(p)) continue;
      if (STREET_LIKE.test(p)) continue;
      if (/^\d/.test(p)) continue;
      if (p.length > 40) continue;
      city = p.replace(/^(?:п\.?|пос\.?|посёлок|с\.?|село|пгт\.?)\s+/i, '').trim();
      break;
    }
  }

  return { city: city || null, region: region || null };
}

/** @deprecated kept for tests / callers; prefer extractPlaceFromAddress */
export function extractCityFromAddress(address) {
  const { city, region } = extractPlaceFromAddress(address);
  return city || region || null;
}

function toMapStation(st) {
  const raw = st.station || st;
  const id = raw.stationId ?? raw.stationID ?? raw.id ?? st.stationId ?? st.stationID ?? st.id;
  const lat = Number(raw.latitude ?? st.latitude);
  const lng = Number(raw.longitude ?? st.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) < 1e-6 && Math.abs(lng) < 1e-6) return null;

  let stationState = raw.stationState ?? st.stationState;
  if (stationState == null || stationState === '') {
    const available = raw.availability ?? raw.available ?? st.availability ?? st.available;
    const status = raw.status ?? st.status;
    const statusUp = status == null ? '' : String(status).toUpperCase();
    const disabled =
      available === false ||
      available === 'false' ||
      statusUp === 'DISABLED' ||
      statusUp === 'INACTIVE' ||
      statusUp === 'OFF' ||
      statusUp === 'BLOCKED';
    if (disabled) stationState = 'STATION_DISCONNECTED';
  }

  // Important: Number(null) === 0 — must not treat missing powerBanks as empty.
  const rawPb = raw.powerBanks ?? st.powerBanks ?? raw.powerBankCount ?? st.powerBankCount;
  const powerBanks = (rawPb === undefined || rawPb === null || rawPb === '')
    ? NaN
    : Number(rawPb);
  const address = raw.address ?? st.address ?? null;
  const place2 = extractPlaceFromAddress(address);
  const place3 = extractPlaceFromAddress(raw.additionalAddress ?? st.additionalAddress);
  const city = place2.city || place3.city || null;
  const region = place2.region || place3.region || null;

  return {
    stationId: id,
    latitude: lat,
    longitude: lng,
    stationState: stationState == null ? null : String(stationState).toUpperCase(),
    powerBanks: Number.isFinite(powerBanks) ? powerBanks : null,
    status: raw.status ?? st.status,
    address,
    city,
    region,
  };
}

/**
 * Pin color:
 * - offline: STATION_DISCONNECTED / disabled status
 * - empty: BE map cache NO_POWER_BANKS (0 banks with status IN_STATION), or known powerBanks===0
 * - online: otherwise (incl. NO_FREE_SLOTS / null state = has banks)
 *
 * Note: public /map/stations has no powerBanks field — only stationState.
 * Full popup powerBanks counts only IN_STATION with battery 81–100%.
 */
function getPinKind(st) {
  const state = (st.stationState != null && st.stationState !== '') ? String(st.stationState).toUpperCase() : null;
  if (state === 'STATION_DISCONNECTED') return 'offline';
  if (state === 'NO_POWER_BANKS') return 'empty';
  if (state === 'NO_FREE_SLOTS') return 'online';
  const status = (st.status != null && st.status !== '') ? String(st.status).toUpperCase() : null;
  const disabled = status === 'DISABLED' || status === 'INACTIVE' || status === 'OFF' || status === 'FALSE' || status === 'BLOCKED';
  if (disabled) return 'offline';
  const count = st.powerBanks != null && st.powerBanks !== '' ? Number(st.powerBanks) : null;
  if (count != null && Number.isFinite(count) && count === 0) return 'empty';
  return 'online';
}

function buildPinIcon(kind, sizePx) {
  const url = kind === 'offline' ? pinOffline : kind === 'empty' ? pinEmpty : pinOnline;
  if (!window.google?.maps) return url;
  const size = Math.max(10, Number(sizePx) || 20);
  return {
    url,
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(size / 2, size / 2),
  };
}

/** Reuse Size/Point objects across re-renders (zoom churn). */
const pinIconMemo = new Map();
function getPinIcon(kind, sizePx) {
  if (!window.google?.maps) return buildPinIcon(kind, sizePx);
  const size = Math.max(10, Number(sizePx) || 20);
  const key = `${kind}:${size}`;
  let icon = pinIconMemo.get(key);
  if (!icon) {
    icon = buildPinIcon(kind, size);
    pinIconMemo.set(key, icon);
  }
  return icon;
}

function mergeMapStations(mapList, adminList) {
  if (!mapList?.length && !adminList?.length) return [];
  if (!mapList?.length) return adminList || [];
  if (!adminList?.length) return mapList;
  const byId = new Map();
  mapList.forEach((st) => byId.set(String(st.stationId), { ...st }));
  adminList.forEach((st) => {
    const key = String(st.stationId);
    const prev = byId.get(key);
    if (!prev) {
      byId.set(key, st);
      return;
    }
    byId.set(key, {
      ...prev,
      ...st,
      stationState: prev.stationState || st.stationState,
      powerBanks: prev.powerBanks != null ? prev.powerBanks : st.powerBanks,
      address: st.address || prev.address,
      city: st.city || prev.city,
      region: st.region || prev.region,
    });
  });
  return Array.from(byId.values());
}

function stationInBounds(st, bounds) {
  if (!bounds || !window.google?.maps) return true;
  const lat = Number(st.latitude);
  const lng = Number(st.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  try {
    return bounds.contains(new window.google.maps.LatLng(lat, lng));
  } catch (_) {
    return true;
  }
}

/** Grid cell size in degrees — larger cells when zoomed out → fewer clusters. */
function cellSizeForZoom(zoom) {
  const z = Number(zoom) || DEFAULT_ZOOM;
  if (z <= 4) return 4;
  if (z <= 5) return 2;
  if (z <= 6) return 1;
  if (z <= 7) return 0.5;
  if (z <= 8) return 0.28;
  if (z <= 9) return 0.14;
  return 0.08;
}

function pickDominantLabel(counts, fallback) {
  let best = null;
  let bestN = 0;
  counts.forEach((n, name) => {
    if (n > bestN) {
      best = name;
      bestN = n;
    }
  });
  return best || fallback;
}

/**
 * Geographic clustering: group nearby stations into grid cells.
 * Label = dominant city, else region, else unknown.
 * This avoids "one cluster per village" around Belgorod at far zoom.
 */
function buildGeoClusters(stations, zoom, unknownLabel) {
  const cell = cellSizeForZoom(zoom);
  const groups = new Map();

  stations.forEach((st) => {
    const lat = Number(st.latitude);
    const lng = Number(st.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const key = `${Math.floor(lat / cell)}_${Math.floor(lng / cell)}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        stations: [],
        online: 0,
        empty: 0,
        offline: 0,
        sumLat: 0,
        sumLng: 0,
        cityCounts: new Map(),
        regionCounts: new Map(),
      };
      groups.set(key, g);
    }
    g.stations.push(st);
    g.sumLat += lat;
    g.sumLng += lng;
    const kind = getPinKind(st);
    if (kind === 'offline') g.offline += 1;
    else if (kind === 'empty') g.empty += 1;
    else g.online += 1;
    if (st.city) g.cityCounts.set(st.city, (g.cityCounts.get(st.city) || 0) + 1);
    if (st.region) g.regionCounts.set(st.region, (g.regionCounts.get(st.region) || 0) + 1);
  });

  return Array.from(groups.values()).map((g) => {
    const n = g.stations.length || 1;
    const city = pickDominantLabel(g.cityCounts, null);
    const region = pickDominantLabel(g.regionCounts, null);
    // Prefer city; if many different cities in one cell, prefer region name.
    const distinctCities = g.cityCounts.size;
    let label = city || region || unknownLabel;
    if (distinctCities >= 3 && region) label = region;
    return {
      id: g.key,
      city: label,
      total: g.stations.length,
      online: g.online,
      empty: g.empty,
      offline: g.offline,
      lat: g.sumLat / n,
      lng: g.sumLng / n,
      stations: g.stations,
    };
  });
}

const INFO_PRIMARY_KEYS = ['stationNumber', 'stationId', 'name', 'address', 'additionalAddress', 'status', 'statusAt', 'numberSlots', 'powerBanks', 'freeSlots'];
const INFO_HIDDEN_KEYS = ['created', 'createdDate', 'timezone', 'available', 'stationState', 'stationSchedule'];
const CROSS_LINK_STATION_KEY = 'crossLinkStation';

function translateStationStatus(status, t) {
  const raw = status == null ? '' : String(status).toUpperCase();
  if (!raw) return '—';
  if (raw === 'ACTIVE' || raw === 'ENABLED' || raw === 'ON' || raw === 'TRUE' || raw === 'CONNECTED') {
    return t('map.statusActive');
  }
  if (raw === 'DISABLED' || raw === 'INACTIVE' || raw === 'OFF' || raw === 'FALSE' || raw === 'BLOCKED') {
    return t('map.statusDisabled');
  }
  return String(status);
}

function StationInfoContent({ info, onClose, onGoToStation, t }) {
  if (!info) {
    return (
      <Box sx={{ minWidth: 220, p: 1, color: '#111', bgcolor: '#fff' }}>
        <Typography variant="body2" sx={{ color: '#666' }}>—</Typography>
        {onClose && (
          <Button size="small" onClick={onClose} sx={{ mt: 1 }}>
            {t('map.closeInfo')}
          </Button>
        )}
      </Box>
    );
  }
  const stationNumber = info.stationNumber ?? info.stationId;
  const primary = {};
  Object.keys(info).forEach((key) => {
    const v = info[key];
    if (v === undefined || v === null) return;
    if (INFO_HIDDEN_KEYS.includes(key)) return;
    if (typeof v === 'object' && v !== null && !Array.isArray(v)) return;
    if (INFO_PRIMARY_KEYS.includes(key)) primary[key] = v;
  });

  return (
    <Box sx={{ minWidth: 260, maxWidth: 360, p: 1, color: '#111', bgcolor: '#fff' }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ color: '#111' }}>
        № {info.stationNumber ?? info.stationId ?? '—'}
      </Typography>
      {primary.name && (
        <Typography variant="body2" sx={{ color: '#555', mb: 0.5 }}>{primary.name}</Typography>
      )}
      {primary.address && (
        <Typography variant="body2" sx={{ color: '#555', mb: 0.5 }}>
          {t('map.address')}: {primary.address}
        </Typography>
      )}
      {primary.additionalAddress && (
        <Typography variant="caption" display="block" sx={{ color: '#777', mb: 0.5 }}>
          {primary.additionalAddress}
        </Typography>
      )}
      {primary.status != null && (
        <Typography variant="caption" display="block" sx={{ color: '#555' }}>
          {t('map.status')}: {translateStationStatus(primary.status, t)}
        </Typography>
      )}
      {primary.statusAt != null && (
        <Typography variant="caption" display="block" sx={{ color: '#555' }}>
          {t('map.statusAt')}: {formatApiDate(primary.statusAt)}
        </Typography>
      )}
      <Typography variant="caption" display="block" sx={{ color: '#555', mb: 1 }}>
        {t('map.slots')}: {info.numberSlots ?? '—'} · {t('map.powerBanks')}: {info.powerBanks ?? '—'} · {t('map.freeSlots')}: {info.freeSlots ?? '—'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
        {stationNumber != null && onGoToStation && (
          <Button
            size="small"
            variant="contained"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onGoToStation();
            }}
          >
            {t('map.openStation')}
          </Button>
        )}
        {onClose && (
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
          >
            {t('map.closeInfo')}
          </Button>
        )}
      </Box>
    </Box>
  );
}

function CityClusterMarker({ cluster, onActivate, t, interactionGuardRef }) {
  const ptrRef = useRef(null);

  return (
    <OverlayViewF
      position={{ lat: cluster.lat, lng: cluster.lng }}
      mapPaneName={OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -(h / 2) })}
    >
      <Box
        onWheel={() => {
          if (interactionGuardRef) {
            interactionGuardRef.current = Date.now() + CLUSTER_CLICK_GUARD_MS;
          }
        }}
        onPointerDown={(e) => {
          ptrRef.current = { x: e.clientX, y: e.clientY };
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (Date.now() < (interactionGuardRef?.current || 0)) return;
          const start = ptrRef.current;
          if (start) {
            const dx = Math.abs(e.clientX - start.x);
            const dy = Math.abs(e.clientY - start.y);
            if (dx > 10 || dy > 10) return;
          }
          onActivate(cluster);
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          transform: 'translateZ(0)',
        }}
      >
        <Box
          sx={{
            minWidth: 40,
            height: 40,
            px: 1,
            borderRadius: '20px',
            bgcolor: '#4FC3F7',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 13,
            boxShadow: '0 2px 8px rgba(0,0,0,0.45)',
            border: '2px solid #fff',
          }}
        >
          {cluster.total}
        </Box>
        <Box
          sx={{
            mt: 0.5,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: 'rgba(20,20,20,0.88)',
            color: '#fff',
            maxWidth: 200,
            textAlign: 'center',
            boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
            pointerEvents: 'none',
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.2 }}>
            {cluster.city}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, lineHeight: 1.3, fontSize: 10 }}>
            {t('map.clusterStations', { count: cluster.total })}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, lineHeight: 1.3, fontSize: 10 }}>
            {t('map.clusterOnline')}: {cluster.online}
            {cluster.empty > 0 ? ` · ${t('map.clusterEmpty')}: ${cluster.empty}` : ''}
            {' · '}
            {t('map.clusterOffline')}: {cluster.offline}
          </Typography>
        </Box>
      </Box>
    </OverlayViewF>
  );
}

async function fetchAdminStations() {
  // Spring PageRequest is 0-based (Stations UI uses page: 0).
  const first = await api.GetStations(0, ADMIN_PAGE_SIZE);
  const firstList = first?.data?.stations ?? [];
  const collected = (Array.isArray(firstList) ? firstList : []).map(toMapStation).filter(Boolean);
  const pageCountRaw = Number(first?.data?.pageCount);
  const pageCount = Number.isFinite(pageCountRaw) && pageCountRaw > 0
    ? Math.min(Math.floor(pageCountRaw), ADMIN_MAX_PAGES)
    : (firstList.length < ADMIN_PAGE_SIZE ? 1 : ADMIN_MAX_PAGES);

  if (pageCount <= 1) return collected;

  const remaining = [];
  for (let page = 1; page < pageCount; page += 1) remaining.push(page);

  for (let i = 0; i < remaining.length; i += ADMIN_FETCH_CONCURRENCY) {
    const chunk = remaining.slice(i, i + ADMIN_FETCH_CONCURRENCY);
    // eslint-disable-next-line no-await-in-loop
    const results = await Promise.all(
      chunk.map((page) => api.GetStations(page, ADMIN_PAGE_SIZE).catch(() => null)),
    );
    results.forEach((res) => {
      const rawList = res?.data?.stations ?? [];
      if (!Array.isArray(rawList) || !rawList.length) return;
      collected.push(...rawList.map(toMapStation).filter(Boolean));
    });
  }
  return collected;
}

async function fetchPublicMapStations() {
  const res = await api.GetStationsOnMap();
  return (Array.isArray(res.data) ? res.data : []).map(toMapStation).filter(Boolean);
}

export default function DashboardMap() {
  const { t } = useTranslation();
  const history = useHistory();
  // App uses CSS-vars color schemes; palette.mode often stays stale — follow useColorScheme like ThemeSwitcher.
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const { mode, systemMode } = useColorScheme();
  const colorMode = (!mode || mode === 'system')
    ? (systemMode || (prefersDarkMode ? 'dark' : 'light'))
    : mode;
  const { isLoaded, loadError, apiKey } = useGoogleMaps();

  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [stationInfo, setStationInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(false);
  const [addressSearch, setAddressSearch] = useState('');
  const [searchError, setSearchError] = useState(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [mapBoundsVersion, setMapBoundsVersion] = useState(0);
  const mapRef = useRef(null);
  const interactionGuardRef = useRef(0);
  const didFitRef = useRef(false);
  const zoomDebounceRef = useRef(null);
  const boundsDebounceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setLoading(true);
    didFitRef.current = false;

    async function load() {
      const role = getCurrentRole();
      const isSuperAdmin = role === 'SUPER_ADMIN';

      try {
        if (isSuperAdmin) {
          // Fast path: lean public map cache paints pins ASAP; admin addresses enrich later.
          let mapList = [];
          try {
            mapList = await fetchPublicMapStations();
          } catch (_) {
            mapList = [];
          }
          if (cancelled) return;
          if (mapList.length) {
            setStations(mapList);
            setLoading(false);
          }

          let adminList = [];
          try {
            adminList = await fetchAdminStations();
          } catch (_) {
            adminList = [];
          }
          if (cancelled) return;
          if (adminList.length || mapList.length) {
            setStations(mergeMapStations(mapList, adminList));
          } else if (!mapList.length) {
            setStations([]);
          }
        } else {
          const adminList = await fetchAdminStations();
          if (cancelled) return;
          setStations(adminList);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || t('map.serverError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
      if (zoomDebounceRef.current) window.clearTimeout(zoomDebounceRef.current);
      if (boundsDebounceRef.current) window.clearTimeout(boundsDebounceRef.current);
    };
  }, [t]);

  const unknownCity = t('map.clusterUnknownCity');
  const showClusters = zoom < CLUSTER_MAX_ZOOM;
  const cityClusters = useMemo(
    () => (showClusters ? buildGeoClusters(stations, zoom, unknownCity) : []),
    [stations, zoom, unknownCity, showClusters],
  );
  const pinSize = pinSizeForZoom(zoom);

  const visibleStations = useMemo(() => {
    if (showClusters) return [];
    const bounds = mapRef.current?.getBounds?.() || null;
    // mapBoundsVersion forces recompute after pan/zoom idle
    void mapBoundsVersion;
    let list = stations;
    if (bounds) {
      list = stations.filter((st) => stationInBounds(st, bounds));
    }
    if (list.length > MAX_VISIBLE_MARKERS) {
      return list.slice(0, MAX_VISIBLE_MARKERS);
    }
    return list;
  }, [stations, showClusters, mapBoundsVersion]);

  const scheduleBoundsRefresh = useCallback(() => {
    if (boundsDebounceRef.current) window.clearTimeout(boundsDebounceRef.current);
    boundsDebounceRef.current = window.setTimeout(() => {
      setMapBoundsVersion((v) => v + 1);
    }, 120);
  }, []);

  const handleMarkerClick = useCallback((stationId, lat, lng) => {
    setSelectedPosition({ lat: Number(lat), lng: Number(lng) });
    setStationInfo(null);
    setInfoLoading(true);
    api
      .GetStationInfoOnMap(stationId)
      .then((res) => setStationInfo(res.data || null))
      .catch(() => setStationInfo(null))
      .finally(() => setInfoLoading(false));
  }, []);

  const handleInfoClose = useCallback(() => {
    setSelectedPosition(null);
    setStationInfo(null);
  }, []);

  const handleGoToStation = useCallback((stationNumberOrId) => {
    try {
      sessionStorage.setItem(CROSS_LINK_STATION_KEY, String(stationNumberOrId));
    } catch (_) {}
    history.push('/station');
  }, [history]);

  const handleAddressSearch = useCallback((e) => {
    e?.preventDefault();
    const query = (addressSearch ?? '').toString().trim();
    if (!query || !mapRef.current || !window.google) return;
    setSearchError(null);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: query }, (results, status) => {
      if (status !== window.google.maps.GeocoderStatus.OK || !results?.[0]) {
        setSearchError(t('map.searchError'));
        return;
      }
      const loc = results[0].geometry.location;
      mapRef.current.panTo({ lat: loc.lat(), lng: loc.lng() });
      mapRef.current.setZoom(15);
      setZoom(15);
    });
  }, [addressSearch, t]);

  const fitMapToStations = useCallback((map, stationList) => {
    if (!map || !stationList?.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    let count = 0;
    stationList.forEach((st) => {
      const lat = Number(st.latitude);
      const lng = Number(st.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        bounds.extend({ lat, lng });
        count += 1;
      }
    });
    if (count === 0) {
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(DEFAULT_ZOOM);
      setZoom(DEFAULT_ZOOM);
      return;
    }
    if (count === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(14);
      setZoom(14);
      return;
    }
    map.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 });
    const z = map.getZoom();
    if (typeof z === 'number') setZoom(z);
  }, []);

  const handleClusterActivate = useCallback((cluster) => {
    if (!mapRef.current || !cluster?.stations?.length) return;
    if (Date.now() < interactionGuardRef.current) return;
    fitMapToStations(mapRef.current, cluster.stations);
    const map = mapRef.current;
    const z = map.getZoom();
    if (typeof z === 'number' && z < CLUSTER_MAX_ZOOM) {
      map.setZoom(CLUSTER_MAX_ZOOM);
      setZoom(CLUSTER_MAX_ZOOM);
    }
  }, [fitMapToStations]);

  const center = useMemo(() => DEFAULT_CENTER, []);

  useEffect(() => {
    if (!mapRef.current || !stations.length || !window.google?.maps) return;
    if (didFitRef.current) return;
    didFitRef.current = true;
    fitMapToStations(mapRef.current, stations);
  }, [stations, fitMapToStations]);

  const mapOptions = useMemo(() => ({
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
    scrollwheel: true,
    gestureHandling: 'greedy',
    styles: colorMode === 'dark' ? DARK_MAP_STYLES : LIGHT_MAP_STYLES,
    backgroundColor: colorMode === 'dark' ? '#1d1d1d' : '#e8e6e1',
    minZoom: 3,
    clickableIcons: false,
  }), [colorMode]);

  const mapTitle = t('dashboard.stationsMap');

  if (!apiKey) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>{mapTitle}</Typography>
        <Typography variant="body2" color="text.secondary">{t('map.setApiKey')}</Typography>
      </Paper>
    );
  }

  if (loadError) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>{mapTitle}</Typography>
        <Typography variant="body2" color="error">{t('map.loadError')}</Typography>
      </Paper>
    );
  }

  if (!isLoaded) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>{mapTitle}</Typography>
        <Typography variant="body2" color="text.secondary">{t('map.loading')}</Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {mapTitle}
      </Typography>
      <Box component="form" onSubmit={handleAddressSearch} sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder={t('map.searchPlaceholder')}
          value={addressSearch}
          onChange={(e) => { setAddressSearch(e.target.value); setSearchError(null); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, maxWidth: 360 }}
        />
        <Button type="submit" variant="outlined" size="small">{t('map.search')}</Button>
      </Box>
      {searchError && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{searchError}</Typography>}
      {error && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>}
      {!error && loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('map.loadingStations')}</Typography>
      )}
      {!error && !loading && stations.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('map.noStations')}</Typography>
      )}
      <Box sx={{ overflow: 'hidden', borderRadius: 1 }}>
        <GoogleMap
          key={`dashboard-map-${colorMode}`}
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={center}
          zoom={DEFAULT_ZOOM}
          options={mapOptions}
          onLoad={(map) => {
            mapRef.current = map;
            const z = map.getZoom();
            if (typeof z === 'number') setZoom(z);
            if (stations.length && !didFitRef.current) {
              didFitRef.current = true;
              fitMapToStations(map, stations);
            }
          }}
          onUnmount={() => { mapRef.current = null; }}
          onZoomChanged={() => {
            interactionGuardRef.current = Date.now() + CLUSTER_CLICK_GUARD_MS;
            if (zoomDebounceRef.current) window.clearTimeout(zoomDebounceRef.current);
            zoomDebounceRef.current = window.setTimeout(() => {
              const z = mapRef.current?.getZoom?.();
              if (typeof z === 'number') setZoom(z);
              scheduleBoundsRefresh();
            }, 80);
          }}
          onDrag={() => {
            interactionGuardRef.current = Date.now() + CLUSTER_CLICK_GUARD_MS;
          }}
          onIdle={scheduleBoundsRefresh}
        >
          {showClusters
            ? cityClusters.map((cluster) => (
              <CityClusterMarker
                key={cluster.id}
                cluster={cluster}
                onActivate={handleClusterActivate}
                interactionGuardRef={interactionGuardRef}
                t={t}
              />
            ))
            : visibleStations.map((st) => {
              const lat = Number(st.latitude);
              const lng = Number(st.longitude);
              if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
              const kind = getPinKind(st);
              return (
                <Marker
                  key={st.stationId || `${lat}-${lng}`}
                  position={{ lat, lng }}
                  icon={getPinIcon(kind, pinSize)}
                  onClick={() => handleMarkerClick(st.stationId, lat, lng)}
                  title={String(st.stationId || '')}
                />
              );
            })}
          {selectedPosition && !showClusters && (
            <InfoWindow
              position={selectedPosition}
              onCloseClick={handleInfoClose}
              options={{ maxWidth: 380, pixelOffset: new window.google.maps.Size(0, -8) }}
            >
              <Box sx={{ p: 0, m: 0, color: '#111', bgcolor: '#fff' }}>
                {infoLoading ? (
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>{t('dashboard.loading')}</Typography>
                  </Box>
                ) : (
                  <StationInfoContent
                    info={stationInfo}
                    onClose={handleInfoClose}
                    onGoToStation={
                      stationInfo?.stationNumber != null
                        ? () => handleGoToStation(stationInfo.stationNumber)
                        : stationInfo?.stationId != null
                          ? () => handleGoToStation(stationInfo.stationId)
                          : undefined
                    }
                    t={t}
                  />
                )}
              </Box>
            </InfoWindow>
          )}
        </GoogleMap>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5, alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {t('map.legendTitle')}:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box component="img" src={pinOnline} alt="" sx={{ width: 16, height: 16 }} />
          <Typography variant="caption" color="text.secondary">{t('map.legendOnline')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box component="img" src={pinEmpty} alt="" sx={{ width: 16, height: 16 }} />
          <Typography variant="caption" color="text.secondary">{t('map.legendNoPowerbanks')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box component="img" src={pinOffline} alt="" sx={{ width: 16, height: 16 }} />
          <Typography variant="caption" color="text.secondary">{t('map.legendOffline')}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
          {showClusters ? t('map.clusterModeHint') : t('map.pinsModeHint')}
        </Typography>
      </Box>
    </Paper>
  );
}
